import { createContext, useContext, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { updateTemplate } from '@/modules/templates/services/templates.service';
import { useGetTemplateDocxsByTemplateId } from '@/modules/templates/hooks/use-template-docx';
import type { Template, TemplateDocx } from '@/modules/templates/types/templates.types';

export type BuilderStep = 1 | 2 | 3;

interface DocxBuilderContextValue {
  // Identifiers
  templateId: string;
  orgSlug: string;
  orgId: string;

  // Template display state
  title: string;
  description: string;
  available: boolean;
  templateSlug: string;

  // Docx
  docx: TemplateDocx | null;
  isDocxLoading: boolean;

  // UI state
  step: BuilderStep;
  replacing: boolean;
  savedCount: number;
  isSavingDetails: boolean;
  isTogglingPublish: boolean;

  // Actions
  setStep: (s: BuilderStep) => void;
  setReplacing: (r: boolean) => void;
  setSavedCount: (n: number) => void;
  onDocxSaved: () => void;
  saveDetails: (title: string, description: string) => Promise<void>;
  togglePublish: () => Promise<void>;
}

const DocxBuilderContext = createContext<DocxBuilderContextValue | null>(null);

export function useDocxBuilder(): DocxBuilderContextValue {
  const ctx = useContext(DocxBuilderContext);
  if (!ctx) throw new Error('useDocxBuilder must be used within DocxBuilderProvider');
  return ctx;
}

interface DocxBuilderProviderProps {
  template: Template;
  orgSlug: string;
  orgId: string;
  children: ReactNode;
}

export function DocxBuilderProvider({
  template,
  orgSlug,
  orgId,
  children,
}: DocxBuilderProviderProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState(template.title);
  const [description, setDescription] = useState(template.description ?? '');
  const [available, setAvailable] = useState(template.available ?? false);
  const [replacing, setReplacing] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [step, setStep] = useState<BuilderStep>(2);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isTogglingPublish, setIsTogglingPublish] = useState(false);

  const { data: docxResult, isLoading: isDocxLoading } = useGetTemplateDocxsByTemplateId(
    template.id
  );
  const docx: TemplateDocx | null = docxResult?.items?.[0] ?? null;

  // Called after a new docx row is created or replaced
  const onDocxSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['templateDocxs', template.id] });
    setReplacing(false);
    setStep(2);
  };

  const saveDetails = async (nextTitle: string, nextDesc: string) => {
    if (nextTitle === title && nextDesc === description) return;
    setIsSavingDetails(true);
    try {
      await updateTemplate(template.id, { Title: nextTitle, Description: nextDesc });
      setTitle(nextTitle);
      setDescription(nextDesc);
      queryClient.invalidateQueries({ queryKey: ['template', template.id] });
      toast({ variant: 'success', title: 'Template details saved' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: err instanceof Error ? err.message : 'Failed to save details',
      });
    } finally {
      setIsSavingDetails(false);
    }
  };

  const togglePublish = async () => {
    const next = !available;
    setIsTogglingPublish(true);
    try {
      await updateTemplate(template.id, { Available: next });
      setAvailable(next);
      queryClient.invalidateQueries({ queryKey: ['template', template.id] });
      toast({
        variant: 'success',
        title: next ? 'Published to your organization' : 'Moved back to draft',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: err instanceof Error ? err.message : 'Failed to update visibility',
      });
    } finally {
      setIsTogglingPublish(false);
    }
  };

  // Replacing a file forces step 1 (shows dropzone regardless of docx)
  const effectiveStep: BuilderStep = replacing ? 1 : step;

  return (
    <DocxBuilderContext.Provider
      value={{
        templateId: template.id,
        templateSlug: template.slug,
        orgSlug,
        orgId,
        title,
        description,
        available,
        docx,
        isDocxLoading,
        step: effectiveStep,
        replacing,
        savedCount,
        isSavingDetails,
        isTogglingPublish,
        setStep,
        setReplacing,
        setSavedCount,
        onDocxSaved,
        saveDetails,
        togglePublish,
      }}
    >
      {children}
    </DocxBuilderContext.Provider>
  );
}
